import React, { useState } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';
import moment from 'moment';

import { scale as initialScale } from '@components/frame';
import Image from '@v2/context/Image/context';
import { font as initialFont } from '@styles/fonts';
import { JOB_STATUSES, isPhoneNative, isTabletOrWeb, formatDateToLocalDateTime } from '@helpers';
import {
  getScheduleAdherenceInfo,
  getPreferredMachine,
  getOperationImageData,
  getProgressBarWidths,
  getScheduleAdherenceDate,
  getActionButtonConfig,
  getDateDisplay,
} from '../helpers';
import {
  TILE_HEIGHT_WEB,
  TILE_HEIGHT_PHONE,
  TILE_MARGIN_BOTTOM,
  IMAGE_WIDTH,
  IMAGE_HEIGHT,
  IMAGE_CONTAINER_WIDTH,
  IMAGE_CONTAINER_HEIGHT,
  IMAGE_CONTAINER_PADDING,
  LINE_HEIGHT,
  BASE_LINE_WIDTH,
  SECTION_MARGIN_LEFT,
  SECTION_MARGIN_TOP,
  PROGRESS_BAR_HEIGHT,
  PROGRESS_BAR_RADIUS,
  ACTION_BUTTON_SIZE,
  ACTION_BUTTON_LABEL_TOP,
  ACTION_BUTTON_MARGIN_LEFT,
  BADGE_BORDER_RADIUS,
  BADGE_MIN_WIDTH,
  BADGE_PADDING_VERTICAL,
  BADGE_PADDING_HORIZONTAL,
  START_JOB_MODAL_WIDTH,
} from '../constants';
import TouchableElement from '@components/common/touchableElement';
import { getBoxShadowStyle } from '@styles/shadow';
import Svg from '@styles/icons/svg';

import StartJobPopup from './StartJobPopup';
import { SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { LevelSubScreen } from '@v2/pages/common/enums';
import TimeDisplayInStaticFormat from '@components/common/TimeDisplayInStaticFormat';
import LayeredModal from '@components/modal/LayeredModal';
import JobMachinesModal from '@v2/pages/machines/modules/now/components/Job/components/JobMachinesModal';
import StatusBadge from '@components/common/StatusBadge';
import { TourTarget } from '@components/tours';
import { WELCOME_TOUR_IDS, DEMO_ID_THRESHOLD } from '@components/tours/config/global/welcome/constants';
import ReadyPcsQuantity from '@components/common/ReadyPcsV2/components/ReadyPcsQuantity';

const OperationInfoTile = ({
  item,
  C,
  onPress,
  width,
  getStatusConfig = () => {},
  hideActionPart,
  jobMainAppTileView = true,
  scale = initialScale,
  font = initialFont,
  statContextStorage = moment.utc().format(),
}) => {
  if (!item || item === 'empty') {
    return null;
  }

  const imgSize = { width: scale(IMAGE_WIDTH), height: scale(IMAGE_HEIGHT) };

  const imageData = {
    ...getOperationImageData({
      item,
      imgSize,
      onPress: () => onPress(item, LevelSubScreen.DOCUMENTS),
      contextId: item.productionOperationId,
    }),
    showImageControl: false,
  };

  const isRunning = item.status === 'Running';

  const [preferredMachineSelectedLocally, setPreferredMachineSelectedLocally] = useState(null);
  const [startJobModalOpen, setStartJobModalOpen] = useState(false);
  const [machinesModalOpen, setMachinesModalOpen] = useState(false);

  const preferredMachine = preferredMachineSelectedLocally
    ? { machineName: preferredMachineSelectedLocally.name }
    : getPreferredMachine(item.machines);

  const jobStatusConfig = getStatusConfig(item.status);

  const baseLineWidth = scale(BASE_LINE_WIDTH);
  const { blockDuration, blackBlock, redBlock, progressWidth, redProgressWidth } = getProgressBarWidths(
    item.totalTimeActualSeconds,
    item.totalTimeScheduledSeconds,
    baseLineWidth
  );

  const NOT_STARTED = item.status === JOB_STATUSES.NOT_STARTED;

  const scheduleAdherenceDate = getScheduleAdherenceDate(item);
  const amountOfDays = moment.utc(statContextStorage).diff(scheduleAdherenceDate, 'day');
  const amountOfMinutes = moment.utc(statContextStorage).diff(scheduleAdherenceDate, 'minute');

  const { badgeText, badgeIcon, badgeLabelText, showBehindError } = getScheduleAdherenceInfo({
    amountOfDays,
    amountOfMinutes,
    isNotStarted: NOT_STARTED,
  });

  const togglePreferredMachines = () => {
    // setMachinesModalOpen(true); //TODO: rescheduling doesn't work so far
  };

  const badgeColor = showBehindError ? 'Chart/Light/Warning' : 'System/Light/Blue';

  const { label: dateLabelKey, value: dateValue } = getDateDisplay(item);
  const dateLabel = `${i18n.t(dateLabelKey)}:`;

  const ScheduledInfoSectionOP = (
    <View
      style={{
        flex: 1,
        marginTop: scale(SECTION_MARGIN_TOP),
      }}
    >
      {/* Line 1: Days badge + label */}
      <View style={{ height: scale(LINE_HEIGHT), flexDirection: 'row', alignItems: 'flex-start', gap: scale(6) }}>
        {badgeIcon ? (
          <Svg icon={badgeIcon} color={badgeColor} width={scale(BADGE_MIN_WIDTH)} height={scale(BADGE_MIN_WIDTH)} />
        ) : (
          <View
            style={{
              backgroundColor: C(badgeColor),
              borderRadius: scale(BADGE_BORDER_RADIUS),
              paddingVertical: scale(BADGE_PADDING_VERTICAL),
              paddingHorizontal: scale(BADGE_PADDING_HORIZONTAL),
              minWidth: scale(BADGE_MIN_WIDTH),
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Text style={[font['Button 1'], { color: C('Label/Light/AlwaysLight Primary') }]}>{badgeText}</Text>
          </View>
        )}
        <Text numberOfLines={1} style={[font['Subhead 2'], { color: C('Label/Light/Tertiary'), lineHeight: scale(BADGE_MIN_WIDTH) }]}>
          {badgeLabelText}
        </Text>
      </View>

      {/* Line 2: Date label */}
      <View style={{ height: scale(LINE_HEIGHT) }}>
        <Text numberOfLines={1} style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>
          {dateLabel}
        </Text>
      </View>

      {/* Line 3: Date value */}
      <View style={{ height: scale(LINE_HEIGHT) }}>
        <Text numberOfLines={1} style={[font.Body, { color: C('Label/Light/Tertiary') }]}>
          {dateValue ? formatDateToLocalDateTime(dateValue) : ' '}
        </Text>
      </View>
    </View>
  );

  const ScheduledInfoSectionMainApp = (
    <View style={[{ flex: 1, justifyContent: 'center' }, isPhoneNative && { paddingLeft: scale(32), marginTop: scale(5) }]}>
      <View style={{ height: scale(LINE_HEIGHT), justifyContent: 'center' }}>
        <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>{i18n.t('Start')}</Text>
      </View>
      <View
        style={[
          { height: scale(LINE_HEIGHT), flexDirection: 'row', alignItems: 'center', gap: scale(6) },
          showBehindError && isPhoneNative && { marginLeft: -scale(22) },
        ]}
      >
        {showBehindError && isPhoneNative && <Svg icon="error" color="Label/Light/Warning" width={scale(16)} height={scale(14)} />}
        <View style={[{ flexDirection: 'row', alignItems: 'center' }]}>
          <Text style={[font['Body Large'], { color: C('Label/Light/Primary') }]}>
            {moment(item.scheduledStartDate).format('DD')} {i18n.t(moment(item.scheduledStartDate).format('MMM'))}{' '}
            {moment(item.scheduledStartDate).format('YYYY')}
          </Text>
          <Text style={[font['Body Large'], { color: C('Label/Light/Tertiary') }]}> {moment(item.scheduledStartDate).format('HH:mm')}</Text>
        </View>
      </View>
      <View style={{ height: scale(LINE_HEIGHT), justifyContent: 'center' }}>
        <Text
          numberOfLines={1}
          style={[
            font.Body,
            {
              color: C('Label/Light/Tertiary'),
            },
          ]}
        >
          {item.linkedNcFiles?.length || 0} {i18n.t('NC File')}
          {(item.linkedNcFiles?.length || 0) !== 1 ? 's' : ''}
        </Text>
      </View>
    </View>
  );

  const ScheduledInfoSection = jobMainAppTileView ? ScheduledInfoSectionMainApp : ScheduledInfoSectionOP;

  const {
    icon: actionButtonIcon,
    label: actionButtonLabelKey,
    bg: actionButtonBg,
    hoverColor: actionButtonHoverColor,
  } = getActionButtonConfig(isRunning, NOT_STARTED);
  const actionButtonLabel = i18n.t(actionButtonLabelKey);

  const JobStatusSectionOP = (
    <View
      style={{
        flexDirection: 'row',
        // marginTop: scale(8),
        alignItems: 'center',
      }}
    >
      {/* Left part: Quantity + Progress + Time */}
      <View
        style={{
          alignSelf: 'stretch',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          paddingTop: scale(SECTION_MARGIN_TOP),
          paddingBottom: scale(TILE_MARGIN_BOTTOM),
        }}
      >
        {/* Quantity */}
        {NOT_STARTED ? (
          <ReadyPcsQuantity
            {...{
              quantityProduced: item.quantityTarget,
              C,
              fontForQuantityProduced: 'Time 1',
              colorForQuantityProduced: 'Label/Light/Tertiary',
            }}
          />
        ) : (
          <ReadyPcsQuantity
            {...{
              quantityProduced: item.quantityProduced,
              quantityTarget: item.quantityTarget,
              C,
              fontForQuantityProduced: 'Time 1',
              fontForQuantityTarget: 'Time 1',
              colorForQuantityProduced: 'Label/Light/Primary',
            }}
          />
        )}

        {/* Progress bar (Running/Paused only) */}
        {!NOT_STARTED && (
          <View
            style={{
              width: baseLineWidth,
              height: scale(PROGRESS_BAR_HEIGHT),
              backgroundColor: C('Background/Light/Chart'),
              borderRadius: scale(PROGRESS_BAR_RADIUS),
              flexDirection: 'row',
            }}
          >
            <View
              style={[
                {
                  width: progressWidth,
                  height: scale(PROGRESS_BAR_HEIGHT),
                  backgroundColor: C(isRunning ? 'ProdJobBatch/Light/Running' : 'ProdJobBatch/Light/Paused'),
                },
                redBlock > 0
                  ? { borderTopLeftRadius: scale(PROGRESS_BAR_RADIUS), borderBottomLeftRadius: scale(PROGRESS_BAR_RADIUS) }
                  : { borderRadius: scale(PROGRESS_BAR_RADIUS) },
              ]}
            />
            <View
              style={[
                {
                  width: redProgressWidth,
                  height: scale(PROGRESS_BAR_HEIGHT),
                  backgroundColor: C('Label/Light/Warning'),
                  borderTopRightRadius: scale(PROGRESS_BAR_RADIUS),
                  borderBottomRightRadius: scale(PROGRESS_BAR_RADIUS),
                },
                blackBlock <= 0 && { borderTopLeftRadius: scale(PROGRESS_BAR_RADIUS), borderBottomLeftRadius: scale(PROGRESS_BAR_RADIUS) },
              ]}
            />
          </View>
        )}

        {/* Elapsed time + status + Scheduled time */}
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: baseLineWidth }}>
          <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
            {!NOT_STARTED && item.totalTimeActualSeconds != null && (
              <>
                <TimeDisplayInStaticFormat
                  {...{
                    color: isRunning ? 'ProdJobBatch/Light/Running' : 'ProdJobBatch/Light/Paused',
                    C,
                    timeValue: item.totalTimeActualSeconds || 0,
                    shouldRunTime: isRunning,
                    fontForDigit: 'Time 1',
                    fontForSecondDigit: 'Time 3',
                  }}
                />
                <Text
                  style={[
                    font['Caption 2'],
                    { color: C(isRunning ? 'ProdJobBatch/Light/Running' : 'ProdJobBatch/Light/Paused'), marginLeft: scale(4) },
                  ]}
                >
                  {isRunning ? i18n.t('Running') : i18n.t('Paused')}
                </Text>
              </>
            )}
          </View>
          {item.totalTimeScheduledSeconds != null && (
            <TimeDisplayInStaticFormat
              {...{
                color: 'Label/Light/Primary',
                C,
                timeValue: item.totalTimeScheduledSeconds || 0,
                fontForDigit: 'Time 1',
                fontForSecondDigit: 'Time 3',
              }}
            />
          )}
        </View>
      </View>

      {/* Right part: Action button */}
      <View style={{ marginLeft: scale(ACTION_BUTTON_MARGIN_LEFT), alignSelf: 'stretch', alignItems: 'center', justifyContent: 'center' }}>
        <TouchableElement
          onPress={() => {
            setStartJobModalOpen(true);
          }}
        >
          {({ boxStyle, pressHoverColor }) => (
            <View style={[boxStyle, { alignItems: 'center' }]}>
              <Svg
                icon={actionButtonIcon}
                width={scale(ACTION_BUTTON_SIZE)}
                height={scale(ACTION_BUTTON_SIZE)}
                color={pressHoverColor(actionButtonHoverColor)}
              />
              <Text
                style={[
                  font['Caption 2'],
                  { color: C(pressHoverColor(actionButtonHoverColor)), position: 'absolute', top: scale(ACTION_BUTTON_LABEL_TOP) },
                ]}
              >
                {actionButtonLabel}
              </Text>
            </View>
          )}
        </TouchableElement>
      </View>
    </View>
  );

  const JobStatusSectionMainApp = (
    <View
      style={[
        {
          justifyContent: 'center',
        },
        isPhoneNative && { marginTop: scale(5) },
      ]}
    >
      {/* Line 1: Status */}
      <View
        style={[
          {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            height: scale(LINE_HEIGHT),
          },
        ]}
      >
        <StatusBadge C={C} config={jobStatusConfig} scale={scale} font={font} />
      </View>

      {/* Line 2: Pcs Ready/Target */}
      <View style={{ height: scale(LINE_HEIGHT), justifyContent: 'center', alignItems: 'flex-end' }}>
        <ReadyPcsQuantity
          {...{
            quantityProduced: item?.quantityProduced,
            quantityTarget: item?.quantityTarget ?? 0,
            C,
            colorForQuantityProduced: 'Label/Light/Primary',
          }}
        />
      </View>

      {/* Line 3: Total Time Scheduled */}
      <View style={{ flexDirection: 'row', height: scale(LINE_HEIGHT), alignItems: 'center' }}>
        {item.totalTimeActualSeconds != null && (
          <TimeDisplayInStaticFormat {...{ color: 'Label/Light/Tertiary', C, timeValue: item.totalTimeActualSeconds }} />
        )}
        {item.totalTimeScheduledSeconds != null && (
          <>
            <Text style={[font['Time 2'], { color: C('Label/Light/Quinary') }]}> / </Text>
            <TimeDisplayInStaticFormat {...{ color: 'Label/Light/Tertiary', C, timeValue: item.totalTimeScheduledSeconds }} />
          </>
        )}
      </View>
    </View>
  );

  const JobStatusSection = jobMainAppTileView ? JobStatusSectionMainApp : JobStatusSectionOP;

  const ImageSection = (
    <View
      style={{
        width: scale(IMAGE_CONTAINER_WIDTH),
        height: scale(IMAGE_CONTAINER_HEIGHT),
        padding: scale(IMAGE_CONTAINER_PADDING),
      }}
    >
      <View
        testID={TEST_IDS.searchScreen.operations.infoTileImage}
        style={{
          width: scale(IMAGE_WIDTH),
          height: scale(IMAGE_HEIGHT),
          borderRadius: scale(SCREEN_BORDER_RADIUS),
          overflow: 'hidden',
        }}
      >
        <Image {...imageData} />
      </View>
    </View>
  );

  const ncFilesCount = item.linkedNcFiles?.length || 0;

  const MainInfoSectionOP = (
    <View
      style={[
        {
          flex: 1,
          marginLeft: scale(SECTION_MARGIN_LEFT),
          marginTop: scale(SECTION_MARGIN_TOP),
          paddingRight: scale(8),
          overflow: 'hidden',
        },
      ]}
    >
      {/* Line 1: Part Description */}
      <View style={{ height: scale(LINE_HEIGHT) }}>
        <Text
          numberOfLines={1}
          style={[
            font.Body,
            {
              color: C('Label/Light/Primary'),
            },
          ]}
        >
          {item.partDescription}
        </Text>
      </View>
      {/* Line 2: Operation Name on Machine */}
      <View style={{ height: scale(LINE_HEIGHT) }}>
        <Text
          numberOfLines={1}
          style={[
            font['Subhead 2'],
            {
              color: C('Label/Light/Tertiary'),
            },
          ]}
        >
          {item.operationName}
          {preferredMachine ? ` ${i18n.t('on')} ${preferredMachine.machineName}` : ''}
        </Text>
      </View>
      {/* Line 3: Production Operation Number (NC Files count) */}
      <View style={{ height: scale(LINE_HEIGHT) }}>
        <Text numberOfLines={1}>
          <Text style={[font.Body, { color: C('Label/Light/Tertiary') }]}>{item.productionOperationNumber} </Text>
          <Text style={[font.Body, { color: C('Label/Light/Tertiary') }]}>
            ({ncFilesCount} {i18n.t('NC File')}
            {ncFilesCount !== 1 ? 's' : ''})
          </Text>
        </Text>
      </View>
    </View>
  );

  const MainInfoSectionMainApp = (
    <View
      style={[
        {
          flex: 1,
          marginLeft: scale(SECTION_MARGIN_LEFT),
          justifyContent: 'center',
        },
      ]}
    >
      {/* Line 1: Production Operation Number */}
      <View
        style={[
          {
            height: scale(LINE_HEIGHT),
            justifyContent: 'center',
          },
        ]}
      >
        <Text
          numberOfLines={1}
          style={[
            font['Headline Large'],
            {
              color: C('Label/Light/Primary'),
            },
          ]}
        >
          {item.productionOperationNumber}
        </Text>
      </View>

      {/* Line 2: Operation Name + optional Behind Error Icon */}
      <View
        style={[
          {
            height: scale(LINE_HEIGHT),
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          },
          showBehindError && isTabletOrWeb && { paddingRight: scale(7) },
        ]}
      >
        <Text
          numberOfLines={1}
          style={[
            font.Body,
            {
              color: C('Label/Light/Secondary'),
            },
          ]}
        >
          {item.operationName}
        </Text>
        {showBehindError && isTabletOrWeb && <Svg icon="error" color="Label/Light/Warning" width={scale(16)} height={scale(14)} />}
      </View>

      {/* Line 3: on Preferred machine */}
      <TouchableElement onPress={togglePreferredMachines}>
        {({ boxStyle }) => (
          <View
            style={[
              boxStyle,
              {
                height: scale(LINE_HEIGHT),
                alignItems: 'center',
                flexDirection: 'row',
                alignSelf: 'flex-start',
                paddingRight: scale(15),
              },
            ]}
          >
            <Text
              numberOfLines={1}
              style={[
                font.Body,
                {
                  color: C(preferredMachine ? 'Label/Light/Secondary' : 'Label/Light/Tertiary'),
                },
              ]}
            >
              {preferredMachine ? `${i18n.t('on')} ${preferredMachine?.machineName}` : i18n.t('Select Machine')}
            </Text>
            <View
              style={[
                boxStyle,
                {
                  marginLeft: scale(5),
                  backgroundColor: C('Button/Light/Secondary'),
                  borderRadius: scale(8),
                  width: scale(17),
                  height: scale(17),
                  justifyContent: 'center',
                  alignItems: 'center',
                  transform: [{ translateY: scale(1) }],
                },
              ]}
            >
              <Svg icon="chevronBottom" height={10} width={10} color={'Button/Light/Secondary Text'} />
            </View>
          </View>
        )}
      </TouchableElement>
    </View>
  );

  const MainInfoSection = jobMainAppTileView ? MainInfoSectionMainApp : MainInfoSectionOP;

  const isDemoItem = item.productionOperationId >= DEMO_ID_THRESHOLD;

  const tileContent = (
    <TouchableElement
      testID={TEST_IDS.searchScreen.operations.infoTile}
      onPress={() => {
        if (onPress) {
          onPress(item);
        }
      }}
    >
      {({ pressHoverColor, boxStyle }) => (
        <View
          style={[
            boxStyle,
            getBoxShadowStyle({ type: 'Narrow', C }),
            {
              backgroundColor: onPress ? C(pressHoverColor('Background/Light/Field')) : C('Background/Light/Field'),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              marginBottom: scale(TILE_MARGIN_BOTTOM),
              width: width || '100%',
              height: scale(isTabletOrWeb ? TILE_HEIGHT_WEB : TILE_HEIGHT_PHONE),
            },
          ]}
        >
          {/* WEB-TABLET  */}
          {isTabletOrWeb && (
            <View style={{ paddingRight: scale(16), flexDirection: 'row', alignItems: 'stretch' }}>
              {/* Left section - Image - web-tablet */}
              {ImageSection}

              {/* Middle section - Contains Block #1 and Block #2 in row - web/tablet */}
              {isTabletOrWeb && (
                <View style={{ flex: 1, flexDirection: 'row' }}>
                  {/* Block #1 - Main info section - web/tablet */}
                  {MainInfoSection}

                  {/* Block #2 - Schedule info section - web/tablet */}
                  {ScheduledInfoSection}

                  {/* Block #3 - Job Status section - web/tablet */}
                  {!hideActionPart && JobStatusSection}
                </View>
              )}
            </View>
          )}

          {/* PHONE */}
          {isPhoneNative && (
            <View style={{ flex: 1 }}>
              <View style={{ flexDirection: 'row', paddingRight: scale(13) }}>
                {ImageSection}
                {MainInfoSection}
              </View>
              <View style={{ flexDirection: 'row', paddingRight: scale(13) }}>
                {/* Block #2 - Schedule info section - mobile */}
                {ScheduledInfoSection}

                {/* Block #3 - Job Status section - mobile */}
                {!hideActionPart && JobStatusSection}
              </View>
            </View>
          )}
        </View>
      )}
    </TouchableElement>
  );

  return (
    <>
      {isDemoItem ? <TourTarget tourId={WELCOME_TOUR_IDS.OP_SCHEDULED_JOB_TILE}>{tileContent}</TourTarget> : tileContent}

      {/* Start Job Modal in Portal */}
      {startJobModalOpen && (
        <LayeredModal
          isVisible={startJobModalOpen}
          onBackdropPress={() => setStartJobModalOpen(false)}
          C={C}
          scale={scale}
          contentStyle={{ width: scale(START_JOB_MODAL_WIDTH) }}
        >
          <StartJobPopup hide={() => setStartJobModalOpen(false)} item={item} scale={scale} font={font} C={C} />
        </LayeredModal>
      )}

      {/* Job Machines Modal */}
      {machinesModalOpen && (
        <LayeredModal
          isVisible={machinesModalOpen}
          onBackdropPress={() => setMachinesModalOpen(false)}
          C={C}
          scale={scale}
          contentStyle={{ width: scale(START_JOB_MODAL_WIDTH), backgroundColor: C('Background/Light/Panel') }}
        >
          <JobMachinesModal
            hide={() => setMachinesModalOpen(false)}
            job={item}
            jobIds={[item.productionOperationId]}
            customOnSelect={(machineNameId) => {
              setPreferredMachineSelectedLocally(machineNameId);
              setMachinesModalOpen(false);
            }}
          />
        </LayeredModal>
      )}
    </>
  );
};

export default OperationInfoTile;
